import type { AuthResponse } from '../api'
import { routes } from '@/shared/config'

export type RedirectBranch = 'active' | 'inactive'
type SubscriptionStatusRedirect =
  | 'active'
  | 'in_trial'
  | 'pending_cancellation'
  | 'former'
  | 'never_subscribed'
  | 'on_hold'

export const REGISTRATION_PROFILE_STEP = 'profile'
const REGISTRATION_PROFILE_STEP_UNLOCK_STORAGE_KEY = 'registration_profile_step_unlocked'
export const AUTH_REDIRECT_QUERY_KEYS = ['selectedprice', 'promocode', 'code'] as const
type SearchParamsInput =
  | URLSearchParams
  | string
  | string[][]
  | Record<string, string>
  | null
  | undefined

const appendSearchParams = (pathname: string, params: URLSearchParams) => {
  const serialized = params.toString()
  return serialized ? `${pathname}?${serialized}` : pathname
}

export function pickAuthRedirectSearchParams(input: SearchParamsInput) {
  const source = input instanceof URLSearchParams ? input : new URLSearchParams(input ?? undefined)
  const nextParams = new URLSearchParams()

  for (const key of AUTH_REDIRECT_QUERY_KEYS) {
    const value = source.get(key)
    if (value) {
      nextParams.set(key, value)
    }
  }

  return nextParams
}

export function buildRegistrationStepPath(
  step: typeof REGISTRATION_PROFILE_STEP,
  input?: SearchParamsInput
) {
  const nextParams = pickAuthRedirectSearchParams(input)
  nextParams.set('step', step)
  return appendSearchParams(routes.registration, nextParams)
}

export function readRegistrationStep(value: string | null) {
  return value === REGISTRATION_PROFILE_STEP ? REGISTRATION_PROFILE_STEP : null
}

export function readRegistrationProfileStepUnlock() {
  if (typeof window === 'undefined') {
    return false
  }

  return window.sessionStorage.getItem(REGISTRATION_PROFILE_STEP_UNLOCK_STORAGE_KEY) === '1'
}

export function unlockRegistrationProfileStep() {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(REGISTRATION_PROFILE_STEP_UNLOCK_STORAGE_KEY, '1')
}

export function clearRegistrationProfileStepUnlock() {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(REGISTRATION_PROFILE_STEP_UNLOCK_STORAGE_KEY)
}

export function isProfileStepRequired(
  response: Pick<AuthResponse, 'nextStep' | 'profileComplete'> & {
    user?: Pick<AuthResponse['user'], 'acceptedUserAgreement'>
  }
) {
  return (
    response.nextStep === 'profile' ||
    response.profileComplete === false ||
    response.user?.acceptedUserAgreement === false
  )
}

export function resolveAuthRedirect(input: {
  subscriptionActive?: boolean
  subscriptionStatus?: string | null
  redirectBranch?: RedirectBranch
  redirectPath?: string | null
}) {
  const normalizedStatus = input.subscriptionStatus?.trim().toLowerCase() as
    | SubscriptionStatusRedirect
    | undefined

  // ЛК переведён в read-only (task-lk-readonly-lite): покупки и смены тарифа нет.
  // Тех, у кого нет действующего права, уводим на прайсинг Академии. on_hold
  // (последний платёж не прошёл) — на страницу подписки, там виден статус.
  if (normalizedStatus === 'former' || normalizedStatus === 'never_subscribed') {
    return routes.academyPricing
  }

  if (normalizedStatus === 'on_hold') {
    return routes.dashboardSubscription
  }

  if (input.redirectBranch === 'inactive' || input.subscriptionActive === false) {
    return routes.academyPricing
  }

  if (input.redirectPath) {
    return input.redirectPath
  }

  if (
    normalizedStatus === 'active' ||
    normalizedStatus === 'in_trial' ||
    normalizedStatus === 'pending_cancellation'
  ) {
    return routes.videos
  }

  const resolvedBranch: RedirectBranch =
    input.redirectBranch ?? (input.subscriptionActive ? 'active' : 'inactive')

  const redirectByBranch: Record<RedirectBranch, string> = {
    active: routes.videos,
    inactive: routes.academyPricing,
  }

  return redirectByBranch[resolvedBranch]
}

export function resolveAuthSuccessPath(response: AuthResponse) {
  if (isProfileStepRequired(response)) {
    return buildRegistrationStepPath(REGISTRATION_PROFILE_STEP)
  }

  return resolveAuthRedirect({
    subscriptionActive: response.user.subscriptionActive,
    subscriptionStatus: response.user.subscriptionStatus,
    redirectBranch: response.redirectBranch,
    redirectPath: response.redirectPath,
  })
}
